import { Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  appRoutes,
  matchAppRoute,
  navigateTo,
  saveAuthReturnPath,
  consumeAuthReturnPath,
  DEFAULT_CLIENT_ID,
  DEFAULT_PROJECT_ID,
  APP_NAV_EVENT,
  type AppRouteKey,
} from '@/app/routes';
import { PageRenderer } from '@/components/app/PageRenderer';
import type { AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { LoginPage } from '@/components/auth/LoginPage';
import { AddAssetModal } from '@/components/layout/AddAssetModal';
import { AppShell as PlatformShell } from '@/components/layout/AppShell';
import { ChatFooterLLMSwitcher } from '@/components/layout/ChatFooterLLMSwitcher';
import { AuthProvider, useAuthContext } from '@/hooks/auth.store';
import { KnowledgeCardProvider } from '@/hooks/knowledge-card.store';
import { KnowledgeTagProvider } from '@/hooks/knowledge-tag.store';
import { useAppNavItems } from '@/hooks/useAppNavItems';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useProjectData } from '@/hooks/useProjectData';

/** Returns the current browser pathname, falling back to '/' in non-browser environments. */
const getPathname = (): string => globalThis.location.pathname || '/';

/**
 * Converts a hyphen-separated assessment or project ID into a human-readable label.
 * Short all-lowercase segments (≤ 4 chars) are fully uppercased; others are title-cased.
 */
const toAssessmentLabel = (assessmentId: string): string =>
  assessmentId
    .split('-')
    .filter(Boolean)
    .map((part) => {
      if (/^[a-z]+$/i.test(part) && part.length <= 4) {
        return part.toUpperCase();
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');

/**
 * Maps the matched app route key to the sidebar navigation item that should appear active.
 */
const getActiveNavKey = (routeKey: AppRouteKey): string => {
  switch (routeKey) {
    case 'portfolio':
      return 'portfolio';
    case 'projects':
      return 'client-projects';
    case 'project-detail':
    case 'project-weekly-updates':
    case 'project-weekly-update-create':
    case 'project-weekly-update-edit':
    case 'project-weekly-update-view':
      return 'project-home';
    case 'project-assessments':
    case 'file-cabinet':
    case 'step-1':
    case 'step-1-generating':
    case 'step-1-output':
    case 'step-1-review':
    case 'step-2':
    case 'step-2-generating':
    case 'step-2-output':
      return 'assessments';
    case 'skills':
    case 'skill-edit':
    case 'skill-template':
      return 'skills';
    case 'workspace':
    case 'asset-library':
    case 'asset-library-note':
      return 'asset-library';
    default:
      return 'portfolio';
  }
};

/**
 * Returns the title and optional subtitle displayed in the TopBar for the given route.
 */
const getPageHeading = (
  routeKey: AppRouteKey,
  assessmentLabel: string,
): { title: string; subtitle?: string } => {
  switch (routeKey) {
    case 'portfolio':
      return { title: 'Portfolio', subtitle: 'Delivery Tracker Portfolio Overview' };
    case 'projects':
      return { title: 'Projects', subtitle: 'Select a project to continue' };
    case 'project-detail':
      return { title: 'Project Home', subtitle: assessmentLabel };
    case 'project-weekly-updates':
    case 'project-weekly-update-create':
    case 'project-weekly-update-edit':
    case 'project-weekly-update-view':
      return { title: 'Weekly Status Update', subtitle: assessmentLabel };
    case 'project-assessments':
      return { title: 'All Assessments', subtitle: assessmentLabel };
    case 'asset-library':
      return { title: 'Asset Library', subtitle: assessmentLabel };
    case 'asset-library-note':
      return { title: 'Create Note', subtitle: assessmentLabel };
    case 'file-cabinet':
      return { title: 'File Cabinet', subtitle: 'Browse and resume assessment work' };
    case 'skills':
      return { title: 'Skills', subtitle: 'Global library' };
    case 'skill-edit':
      return { title: 'Edit skill', subtitle: 'Global library' };
    case 'skill-template':
      return { title: 'Skill template', subtitle: 'Global library' };
    case 'step-1':
      return { title: 'Step 01: Interview Prep', subtitle: assessmentLabel };
    case 'step-1-generating':
      return { title: 'Step 01: Generating', subtitle: assessmentLabel };
    case 'step-1-output':
      return { title: 'Step 01: Output', subtitle: assessmentLabel };
    case 'step-1-review':
      return { title: 'Step 01: Interview Guide - Review', subtitle: assessmentLabel };
    case 'step-2':
      return { title: 'Step 02: Interview Guide', subtitle: assessmentLabel };
    case 'step-2-generating':
      return { title: 'Step 02: Generating', subtitle: assessmentLabel };
    case 'step-2-output':
      return { title: 'Step 02: Output', subtitle: assessmentLabel };
    case 'workspace':
      return { title: 'Assessment Workspace', subtitle: assessmentLabel };
    case 'settings-profile':
      return { title: 'Settings', subtitle: 'Profile' };
    default:
      return { title: 'Home' };
  }
};

/**
 * Root component for the authenticated session. Wires route-scoped hooks and delegates
 * page rendering to PageRenderer.
 */
const AuthenticatedApp: React.FC = () => {
  const {
    user,
    accessToken,
    microsoftOboAssertion,
    logout,
    startSso,
    refreshMicrosoftOboAssertion,
  } = useAuthContext();

  const [pathname, setPathname] = useState<string>(getPathname);
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState<boolean>(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState<boolean>(false);

  const matchedRoute = useMemo(() => matchAppRoute(pathname), [pathname]);
  const selectedClientId = matchedRoute.clientId;
  const selectedProjectId = matchedRoute.projectId;
  const isProjectScopedRoute = Boolean(selectedClientId && selectedProjectId);
  const effectiveClientId = selectedClientId ?? DEFAULT_CLIENT_ID;
  const effectiveProjectId = selectedProjectId ?? DEFAULT_PROJECT_ID;

  // Route-derived labels
  const assessmentLabel = useMemo(
    () => (selectedProjectId ? toAssessmentLabel(selectedProjectId) : 'Workspace'),
    [selectedProjectId],
  );
  const [clientNameById, setClientNameById] = useState<Record<string, string>>({});
  const [projectNameById, setProjectNameById] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!accessToken) {
      setClientNameById({});
      setProjectNameById({});
    }
  }, [accessToken]);
  const cachedClientName = selectedClientId ? (clientNameById[selectedClientId] ?? null) : null;
  const cachedProjectName = selectedProjectId ? (projectNameById[selectedProjectId] ?? null) : null;

  // ProjectHomePage fetches a superset of this data itself (useProjectHome) and reports it back
  // via handleProjectMetadataResolved below — skip the redundant include=metadata fetch there.
  const skipMetadataFetch = matchedRoute.key === 'project-detail';
  const {
    projectSharepointReady,
    selectedProjectName: liveProjectName,
    selectedClientName: liveClientName,
    previousWeeklyUpdate,
    setPreviousWeeklyUpdate,
  } = useProjectData({
    accessToken,
    isProjectScopedRoute,
    selectedClientId,
    selectedProjectId,
    skipMetadataFetch,
  });

  const handleProjectMetadataResolved = useCallback(
    (
      resolvedClientId: string,
      resolvedProjectId: string,
      names: { clientName: string; projectName: string },
    ) => {
      setClientNameById((prev) => ({ ...prev, [resolvedClientId]: names.clientName }));
      setProjectNameById((prev) => ({ ...prev, [resolvedProjectId]: names.projectName }));
    },
    [],
  );

  // Cache (from Portfolio/ClientProjects data already fetched) takes priority over the live
  // per-route fetch, which itself covers a cold deep link before the cache is populated.
  const clientLabel = useMemo(() => {
    const resolved = cachedClientName ?? liveClientName;
    if (resolved) {
      return resolved;
    }
    if (selectedClientId) {
      return toAssessmentLabel(selectedClientId);
    }
    return 'Clients';
  }, [selectedClientId, cachedClientName, liveClientName]);
  const selectedProjectName = cachedProjectName ?? liveProjectName;

  const breadcrumbs = useBreadcrumbs({
    routeKey: matchedRoute.key,
    selectedClientId,
    selectedProjectId,
    clientLabel,
    assessmentLabel,
    selectedProjectName,
  });

  const pageHeading = useMemo(() => {
    const base = getPageHeading(matchedRoute.key, assessmentLabel);
    if (matchedRoute.assessmentInstanceId && matchedRoute.key.startsWith('step-1')) {
      return { ...base, subtitle: `${assessmentLabel} · New assessment` };
    }
    if (matchedRoute.assessmentInstanceId && matchedRoute.key === 'step-2') {
      return { ...base, subtitle: `${assessmentLabel} · Interview guide` };
    }
    return base;
  }, [matchedRoute.key, matchedRoute.assessmentInstanceId, assessmentLabel]);

  const { topNavItems, workspaceNavItems } = useAppNavItems({
    accessToken,
    selectedClientId,
    selectedProjectId,
    clientName: selectedClientId ? clientLabel : undefined,
  });

  const navigateAssessmentFlowStep = useCallback(
    (instanceId: string, step: AssessmentFlowStep) => {
      const stepRoutes: Record<
        AssessmentFlowStep,
        (id: string, project: string, client: string) => string
      > = {
        1: appRoutes.projectAssessmentStep1,
        '1-review': appRoutes.projectAssessmentStep1Review,
        2: appRoutes.projectAssessmentStep2,
        '2-generating': appRoutes.projectAssessmentStep2Generating,
        '2-review': appRoutes.projectAssessmentStep2Output,
      };
      navigateTo(stepRoutes[step](instanceId, effectiveProjectId, effectiveClientId));
    },
    [effectiveClientId, effectiveProjectId],
  );

  // Chat footer visibility
  const chatEnabled = Boolean(accessToken);
  const chatFooterVisibleRouteKeys: AppRouteKey[] = [
    'portfolio',
    'projects',
    'project-detail',
    'project-weekly-updates',
    'project-weekly-update-create',
    'project-weekly-update-edit',
    'project-weekly-update-view',
    'project-assessments',
    'asset-library',
    'asset-library-note',
    'skills',
    'skill-edit',
    'skill-template',
    'file-cabinet',
    'step-1',
    'step-1-generating',
    'step-1-output',
    'step-1-review',
    'step-2',
    'step-2-generating',
    'step-2-output',
    'workspace',
    'settings-profile',
  ];
  const shouldRenderChatFooter =
    chatEnabled && chatFooterVisibleRouteKeys.includes(matchedRoute.key);
  const footerProjectId = matchedRoute.key.startsWith('settings-')
    ? 'settings'
    : effectiveProjectId;
  const footerClientId = matchedRoute.key.startsWith('settings-')
    ? DEFAULT_CLIENT_ID
    : effectiveClientId;

  // URL sync
  useEffect(() => {
    const syncLocation = () => setPathname(getPathname());
    globalThis.addEventListener('popstate', syncLocation);
    globalThis.addEventListener(APP_NAV_EVENT, syncLocation);
    return () => {
      globalThis.removeEventListener('popstate', syncLocation);
      globalThis.removeEventListener(APP_NAV_EVENT, syncLocation);
    };
  }, []);

  // Save return path before any redirect
  useEffect(() => {
    if (
      matchedRoute.key !== 'login' &&
      matchedRoute.key !== 'callback' &&
      matchedRoute.key !== 'not-found'
    ) {
      saveAuthReturnPath();
    }
  }, [matchedRoute.key]);

  // Redirect not-found and post-auth routes
  useEffect(() => {
    if (matchedRoute.key === 'not-found') {
      navigateTo(appRoutes.portfolio, true);
      return;
    }
    if (matchedRoute.key === 'login') {
      const next = consumeAuthReturnPath() ?? appRoutes.portfolio;
      navigateTo(next, true);
    }
  }, [matchedRoute.key]);

  // Close chat panel when auth is lost
  useEffect(() => {
    if (!chatEnabled && isChatPanelOpen) {
      setIsChatPanelOpen(false);
    }
  }, [chatEnabled, isChatPanelOpen]);

  // Close add-asset modal when leaving project scope
  useEffect(() => {
    if (!isProjectScopedRoute && isAddAssetModalOpen) {
      setIsAddAssetModalOpen(false);
    }
  }, [isAddAssetModalOpen, isProjectScopedRoute]);

  return (
    <KnowledgeCardProvider>
      <KnowledgeTagProvider>
        <PlatformShell
          topNavItems={topNavItems}
          workspaceNavItems={workspaceNavItems}
          activeNavKey={getActiveNavKey(matchedRoute.key)}
          onNavigate={(path) => navigateTo(path)}
          onOpenProfile={() => navigateTo(appRoutes.settingsProfile)}
          breadcrumbs={breadcrumbs}
          title={pageHeading.title}
          subtitle={pageHeading.subtitle}
          userName={user?.getDisplayName() ?? 'Unknown User'}
          onSignOut={logout}
          showAddAsset={isProjectScopedRoute}
          onOpenAddAsset={isProjectScopedRoute ? () => setIsAddAssetModalOpen(true) : undefined}
          chatPanelOpen={isChatPanelOpen}
          footer={
            shouldRenderChatFooter ? (
              <ChatFooterLLMSwitcher
                accessToken={accessToken ?? ''}
                clientId={footerClientId}
                projectId={footerProjectId}
                onCreateNoteNavigate={(assetSessionId) =>
                  navigateTo(
                    appRoutes.projectAssetLibrarySession(
                      assetSessionId,
                      footerProjectId,
                      footerClientId,
                    ),
                  )
                }
                chatPanelOpen={isChatPanelOpen}
                onChatPanelOpenChange={setIsChatPanelOpen}
              />
            ) : null
          }
        >
          <PageRenderer
            routeKey={matchedRoute.key}
            assessmentInstanceId={matchedRoute.assessmentInstanceId}
            assetSessionId={matchedRoute.assetSessionId}
            week={matchedRoute.week}
            skillTemplateKey={matchedRoute.skillTemplateKey}
            skillVersionId={matchedRoute.skillVersionId}
            effectiveClientId={effectiveClientId}
            effectiveProjectId={effectiveProjectId}
            selectedClientId={selectedClientId}
            selectedProjectId={selectedProjectId}
            accessToken={accessToken}
            microsoftOboAssertion={microsoftOboAssertion}
            projectSharepointReady={projectSharepointReady}
            clientLabel={clientLabel}
            assessmentLabel={assessmentLabel}
            selectedProjectName={selectedProjectName}
            user={user}
            startSso={startSso}
            refreshMicrosoftOboAssertion={refreshMicrosoftOboAssertion}
            previousWeeklyUpdate={previousWeeklyUpdate}
            setPreviousWeeklyUpdate={setPreviousWeeklyUpdate}
            navigateAssessmentFlowStep={navigateAssessmentFlowStep}
            onClientNamesResolved={setClientNameById}
            onProjectNamesResolved={setProjectNameById}
            onProjectMetadataResolved={handleProjectMetadataResolved}
            clientNameById={clientNameById}
            onOpenAddAsset={() => setIsAddAssetModalOpen(true)}
          />
        </PlatformShell>
        <AddAssetModal
          open={isAddAssetModalOpen}
          onClose={() => setIsAddAssetModalOpen(false)}
          sharepointReady={projectSharepointReady}
          accessToken={accessToken}
          microsoftOboAssertion={microsoftOboAssertion}
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          onMicrosoftReauth={startSso}
          onMicrosoftRefreshObo={refreshMicrosoftOboAssertion}
          onWordDocumentUploaded={(cardId) =>
            navigateTo(
              appRoutes.projectAssetLibrarySession(cardId, effectiveProjectId, effectiveClientId),
            )
          }
          onCreateNoteNavigate={(assetSessionId) =>
            navigateTo(
              appRoutes.projectAssetLibrarySession(
                assetSessionId,
                effectiveProjectId,
                effectiveClientId,
              ),
            )
          }
        />
      </KnowledgeTagProvider>
    </KnowledgeCardProvider>
  );
};

/**
 * Guards the app behind Microsoft Entra SSO authentication: shows a spinner during session
 * restoration, the LoginPage when unauthenticated, or AuthenticatedApp once signed in.
 */
const AppRouter: React.FC = () => {
  const { isAuthenticated, status, error, startSso } = useAuthContext();
  const [pathname, setPathname] = useState<string>(getPathname);
  const matchedRoute = useMemo(() => matchAppRoute(pathname), [pathname]);

  useEffect(() => {
    const syncLocation = () => setPathname(getPathname());
    globalThis.addEventListener('popstate', syncLocation);
    globalThis.addEventListener(APP_NAV_EVENT, syncLocation);
    return () => {
      globalThis.removeEventListener('popstate', syncLocation);
      globalThis.removeEventListener(APP_NAV_EVENT, syncLocation);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    if (matchedRoute.key === 'login' || matchedRoute.key === 'callback') {
      return;
    }
    if (
      status === 'initializing' ||
      status === 'restoring' ||
      status === 'processing-callback' ||
      status === 'redirecting'
    ) {
      return;
    }
    if (status !== 'unauthenticated' && status !== 'error') {
      return;
    }
    saveAuthReturnPath();
    navigateTo(appRoutes.login, true);
  }, [isAuthenticated, matchedRoute.key, status]);

  if (!isAuthenticated) {
    if (status === 'initializing' || status === 'restoring' || status === 'processing-callback') {
      return (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            height: '100vh',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" tip="Checking your session…" />
        </div>
      );
    }
    return <LoginPage status={status} error={error} onSignIn={startSso} />;
  }

  return <AuthenticatedApp />;
};

/**
 * Application root that wraps AppRouter in the AuthProvider context.
 */
export const App: React.FC = () => (
  <AuthProvider>
    <AppRouter />
  </AuthProvider>
);

export default App;
